(() => {
    const config = window.CUSTOM_ORDER_CONFIG || {};
    const apiBase = config.apiBase || "/apps/custom-order";
    const prepayAmount = Number(config.prepayAmount || 20000);
    const SAVE_DELAY = 250;

    const state =
        window.__CUSTOM_ORDER_FORM_STATE ||
        (window.__CUSTOM_ORDER_FORM_STATE = {
            initialized: false,
            name: "",
            lastname: "",
            phone: "+380",
            email: "",
            city: "",
            cityRef: "",
            warehouse: "",
            warehouseRef: "",
            comment: "",
            payment: "prepay",
        });

    const runtime =
        window.__CUSTOM_ORDER_RUNTIME ||
        (window.__CUSTOM_ORDER_RUNTIME = {
            syncTimer: null,
            saveTimer: null,
            cityTimer: null,
            warehouseTimer: null,
            cityAbort: null,
            warehouseAbort: null,
            saveInFlight: false,
            pendingSave: false,
            lastSavedHash: "",
            globalsBound: false,
            isSubmitting: false,
        });

    function getDom() {
        const wrapper = document.getElementById("custom-order-form");
        if (!wrapper) return null;

        const form =
            wrapper.closest("form") || document.getElementById("cart-form");

        return {
            wrapper,
            form,
            submitBtn: document.getElementById("custom-checkout-btn"),
            subtotalBox: document.querySelector("[data-subtotal-cents]"),
            prepayRow: document.querySelector("[data-prepay-row]"),
            payableTotalEl: document.querySelector("[data-payable-total]"),
            totalLabelEl: document.querySelector("[data-total-label]"),
            cityDropdown: document.getElementById("city-dropdown"),
            warehouseDropdown: document.getElementById("warehouse-dropdown"),
            paymentRadios: Array.from(
                wrapper.querySelectorAll('input[name="payment"]'),
            ),
            fields: {
                name: wrapper.querySelector(
                    'input[name="attributes[FirstName]"]',
                ),
                lastname: wrapper.querySelector(
                    'input[name="attributes[LastName]"]',
                ),
                phone: wrapper.querySelector('input[name="attributes[Phone]"]'),
                email: wrapper.querySelector('input[name="attributes[Email]"]'),
                city: wrapper.querySelector('input[name="attributes[City]"]'),
                cityRef: wrapper.querySelector(
                    'input[name="attributes[CityRef]"]',
                ),
                warehouse: wrapper.querySelector(
                    'input[name="attributes[Warehouse]"]',
                ),
                warehouseRef: wrapper.querySelector(
                    'input[name="attributes[WarehouseRef]"]',
                ),
                comment: wrapper.querySelector(
                    'textarea[name="attributes[Comment]"]',
                ),
                paymentHidden: wrapper.querySelector(
                    'input[name="attributes[PaymentType]"]',
                ),
            },
        };
    }

    function normalizePhone(value) {
        return String(value || "").replace(/\D/g, "");
    }

    // Reduce any Ukrainian phone input (E.164, domestic 0XX, spaced autofill,
    // pasted "+38 (0XX) ...", malformed "+380 0XX ...") down to the 9-digit
    // national subscriber number without the trunk zero. Returns "" .. 9 digits.
    function extractUaPhone(value) {
        let digits = normalizePhone(value);

        if (digits.startsWith("00")) digits = digits.slice(2); // 00380XXXXXXXXX

        // "3" / "38" is only a half-typed country code (e.g. backspacing into
        // the "+380" prefix) — never the start of a national number.
        if (digits === "3" || digits === "38") return "";

        if (digits.startsWith("380")) digits = digits.slice(3);
        else if (digits.startsWith("80")) digits = digits.slice(2);
        else if (digits.startsWith("0")) digits = digits.slice(1);

        // strip trunk / stray leading zeros left by malformed autofill
        // (e.g. "+380 06 372 33 84" -> national "6372..." -> too short -> invalid)
        digits = digits.replace(/^0+/, "");

        return digits.slice(0, 9);
    }

    function formatPhone(value) {
        const nat = extractUaPhone(value);

        if (!nat.length) return "+380";

        const parts = [
            nat.slice(0, 2),
            nat.slice(2, 5),
            nat.slice(5, 7),
            nat.slice(7, 9),
        ].filter(Boolean);

        return `+380 ${parts.join(" ")}`.trim();
    }

    // Canonical value for the backend / Shopify orderCreate (E.164).
    function phoneToE164(value) {
        const nat = extractUaPhone(value);
        return nat.length === 9 ? `+380${nat}` : "";
    }

    function isValidPhone(value) {
        return phoneToE164(value) !== "";
    }

    function isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    }

    function formatMoney(cents) {
        return new Intl.NumberFormat("uk-UA", {
            style: "currency",
            currency: "UAH",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format((Number(cents) || 0) / 100);
    }

    function setFieldValue(field, value) {
        if (!field) return;
        const next = value == null ? "" : String(value);
        if (field.value !== next) field.value = next;
    }

    function getPaymentValue(dom = getDom()) {
        if (!dom) return state.payment || "prepay";

        return (
            dom.wrapper.querySelector('input[name="payment"]:checked')?.value ||
            dom.fields.paymentHidden?.value ||
            state.payment ||
            "prepay"
        );
    }

    function getSubtotalCents(dom = getDom()) {
        return Number(dom?.subtotalBox?.dataset?.subtotalCents || 0);
    }

    function initStateFromDom(dom = getDom()) {
        if (!dom || state.initialized) return;

        state.name = dom.fields.name?.value || "";
        state.lastname = dom.fields.lastname?.value || "";
        state.phone = formatPhone(dom.fields.phone?.value || "");
        state.email = dom.fields.email?.value || "";
        state.city = dom.fields.city?.value || "";
        state.cityRef =
            dom.fields.cityRef?.value ||
            dom.fields.city?.dataset?.cityRef ||
            "";
        state.warehouse = dom.fields.warehouse?.value || "";
        state.warehouseRef =
            dom.fields.warehouseRef?.value ||
            dom.fields.warehouse?.dataset?.warehouseRef ||
            "";
        state.comment = dom.fields.comment?.value || "";
        state.payment = getPaymentValue(dom);
        state.initialized = true;
    }

    function saveStateFromDom(dom = getDom()) {
        if (!dom) return;

        state.name = dom.fields.name?.value || "";
        state.lastname = dom.fields.lastname?.value || "";
        state.phone = formatPhone(dom.fields.phone?.value || "");
        state.email = dom.fields.email?.value || "";
        state.city = dom.fields.city?.value || "";
        state.cityRef =
            dom.fields.cityRef?.value ||
            dom.fields.city?.dataset?.cityRef ||
            "";
        state.warehouse = dom.fields.warehouse?.value || "";
        state.warehouseRef =
            dom.fields.warehouseRef?.value ||
            dom.fields.warehouse?.dataset?.warehouseRef ||
            "";
        state.comment = dom.fields.comment?.value || "";
        state.payment = getPaymentValue(dom);
        state.initialized = true;
    }

    function applyStateToDom(dom = getDom()) {
        if (!dom) return;

        setFieldValue(dom.fields.name, state.name);
        setFieldValue(dom.fields.lastname, state.lastname);

        // The 250ms re-render sync must not clobber a value the browser
        // autofilled while no input/change event fired yet. If the field now
        // holds a different valid number, adopt it instead of overwriting.
        if (
            dom.fields.phone &&
            document.activeElement !== dom.fields.phone &&
            extractUaPhone(dom.fields.phone.value) !==
                extractUaPhone(state.phone) &&
            isValidPhone(dom.fields.phone.value)
        ) {
            state.phone = formatPhone(dom.fields.phone.value);
            queueCartSave();
        }

        setFieldValue(dom.fields.phone, state.phone || "+380");
        setFieldValue(dom.fields.email, state.email);
        setFieldValue(dom.fields.city, state.city);
        setFieldValue(dom.fields.cityRef, state.cityRef);
        setFieldValue(dom.fields.warehouse, state.warehouse);
        setFieldValue(dom.fields.warehouseRef, state.warehouseRef);
        setFieldValue(dom.fields.comment, state.comment);
        setFieldValue(dom.fields.paymentHidden, state.payment || "prepay");

        if (dom.fields.city) {
            dom.fields.city.dataset.cityRef = state.cityRef || "";
        }

        if (dom.fields.warehouse) {
            dom.fields.warehouse.dataset.warehouseRef =
                state.warehouseRef || "";
            dom.fields.warehouse.disabled = !state.cityRef;
            dom.fields.warehouse.placeholder = state.cityRef
                ? "Почніть вводити відділення"
                : "Спочатку оберіть місто";
        }

        dom.paymentRadios.forEach((radio) => {
            radio.checked = radio.value === (state.payment || "prepay");
        });

        updatePaymentSummary(dom);
    }

    function setError(fieldName, message, dom = getDom()) {
        if (!dom) return;

        const fieldMap = {
            name: dom.fields.name,
            lastname: dom.fields.lastname,
            phone: dom.fields.phone,
            email: dom.fields.email,
            city: dom.fields.city,
            warehouse: dom.fields.warehouse,
        };

        const input = fieldMap[fieldName];
        const error = dom.wrapper.querySelector(
            `[data-error-for="${fieldName}"]`,
        );

        if (input) input.classList.add("is-invalid");
        if (error) error.textContent = message || "";
    }

    function clearError(fieldName, dom = getDom()) {
        if (!dom) return;

        const fieldMap = {
            name: dom.fields.name,
            lastname: dom.fields.lastname,
            phone: dom.fields.phone,
            email: dom.fields.email,
            city: dom.fields.city,
            warehouse: dom.fields.warehouse,
        };

        const input = fieldMap[fieldName];
        const error = dom.wrapper.querySelector(
            `[data-error-for="${fieldName}"]`,
        );

        if (input) input.classList.remove("is-invalid");
        if (error) error.textContent = "";
    }

    function clearAllErrors(dom = getDom()) {
        if (!dom) return;

        ["name", "lastname", "phone", "email", "city", "warehouse"].forEach(
            (field) => {
                clearError(field, dom);
            },
        );
    }

    function validatePhoneField(dom = getDom()) {
        if (!dom || !dom.fields.phone) return false;

        const raw = dom.fields.phone.value.trim();

        if (!raw || raw === "+380") {
            clearError("phone", dom);
            return false;
        }

        if (!isValidPhone(raw)) {
            setError(
                "phone",
                "Невірний номер. Формат: +380 XX XXX XX XX",
                dom,
            );
            return false;
        }

        clearError("phone", dom);
        return true;
    }

    function closeDropdown(dropdown) {
        if (!dropdown) return;
        dropdown.hidden = true;
        dropdown.innerHTML = "";
    }

    function closeAllDropdowns(dom = getDom()) {
        if (!dom) return;
        closeDropdown(dom.cityDropdown);
        closeDropdown(dom.warehouseDropdown);
    }

    function renderDropdown(dropdown, items, onSelect) {
        if (!dropdown) return;

        if (!Array.isArray(items) || !items.length) {
            dropdown.innerHTML =
                '<div class="ci-dropdown-item">Нічого не знайдено</div>';
            dropdown.hidden = false;
            return;
        }

        dropdown.innerHTML = items
            .map((item, index) => {
                const ref = String(item.ref || "").replace(/"/g, "&quot;");
                const label = String(item.label || "").replace(/"/g, "&quot;");

                return `
          <div
            class="ci-dropdown-item ${index === 0 ? "is-active" : ""}"
            data-ref="${ref}"
            data-label="${label}"
          >
            ${item.label || ""}
          </div>
        `;
            })
            .join("");

        dropdown.hidden = false;

        dropdown
            .querySelectorAll(".ci-dropdown-item[data-ref]")
            .forEach((node) => {
                node.addEventListener("click", () => {
                    onSelect(node.dataset.ref || "", node.dataset.label || "");
                });
            });
    }

    async function fetchJson(url, signal) {
        const res = await fetch(url, {
            method: "GET",
            headers: { Accept: "application/json" },
            signal,
        });

        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
    }

    function buildAttributesPayload() {
        return {
            FirstName: state.name.trim(),
            LastName: state.lastname.trim(),
            Phone: phoneToE164(state.phone) || state.phone.trim(),
            Email: state.email.trim(),
            City: state.city.trim(),
            CityRef: state.cityRef || "",
            Warehouse: state.warehouse.trim(),
            WarehouseRef: state.warehouseRef || "",
            Comment: state.comment || "",
            PaymentType: state.payment || "prepay",
        };
    }

    async function persistCartAttributes({ force = false } = {}) {
        const attributes = buildAttributesPayload();
        const hash = JSON.stringify(attributes);

        if (!force && hash === runtime.lastSavedHash) return;

        if (runtime.saveInFlight) {
            runtime.pendingSave = true;
            return;
        }

        runtime.saveInFlight = true;

        try {
            const res = await fetch("/cart/update.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                keepalive: true,
                body: JSON.stringify({ attributes }),
            });

            if (!res.ok) {
                throw new Error(`Cart update failed: ${res.status}`);
            }

            runtime.lastSavedHash = hash;
        } catch (error) {
            console.error("Cart attributes save failed", error);
        } finally {
            runtime.saveInFlight = false;

            if (runtime.pendingSave) {
                runtime.pendingSave = false;
                await persistCartAttributes({ force: true });
            }
        }
    }

    function queueCartSave(immediate = false) {
        clearTimeout(runtime.saveTimer);

        if (immediate) {
            persistCartAttributes();
            return;
        }

        runtime.saveTimer = setTimeout(() => {
            persistCartAttributes();
        }, SAVE_DELAY);
    }

    function updatePaymentSummary(dom = getDom()) {
        if (!dom || !dom.subtotalBox) return;

        const subtotal = getSubtotalCents(dom);
        const payment = state.payment || getPaymentValue(dom);
        const payable =
            payment === "prepay"
                ? Math.max(subtotal - prepayAmount, 0)
                : subtotal;

        if (dom.prepayRow) {
            dom.prepayRow.hidden = payment !== "prepay";
        }

        if (dom.totalLabelEl) {
            dom.totalLabelEl.textContent =
                payment === "prepay" ? "При отриманні:" : "До сплати:";
        }

        if (dom.payableTotalEl) {
            dom.payableTotalEl.textContent = formatMoney(payable);
        }
    }

    function resetWarehouse(dom = getDom()) {
        if (!dom || !dom.fields.warehouse) return;

        state.warehouse = "";
        state.warehouseRef = "";

        setFieldValue(dom.fields.warehouse, "");
        setFieldValue(dom.fields.warehouseRef, "");
        dom.fields.warehouse.dataset.warehouseRef = "";
        dom.fields.warehouse.disabled = true;
        dom.fields.warehouse.placeholder = "Спочатку оберіть місто";

        closeDropdown(dom.warehouseDropdown);
    }

    function handleCityInput(dom = getDom()) {
        if (!dom || !dom.fields.city) return;

        clearError("city", dom);

        state.city = dom.fields.city.value.trim();
        state.cityRef = "";
        setFieldValue(dom.fields.cityRef, "");
        dom.fields.city.dataset.cityRef = "";

        resetWarehouse(dom);
        saveStateFromDom(dom);
        queueCartSave();

        const query = dom.fields.city.value.trim();

        if (query.length < 2) {
            closeDropdown(dom.cityDropdown);
            return;
        }

        clearTimeout(runtime.cityTimer);

        if (runtime.cityAbort) {
            runtime.cityAbort.abort();
        }

        runtime.cityTimer = setTimeout(async () => {
            const liveDom = getDom();
            if (!liveDom || !liveDom.fields.city) return;

            const currentQuery = liveDom.fields.city.value.trim();

            if (currentQuery.length < 2) {
                closeDropdown(liveDom.cityDropdown);
                return;
            }

            const controller = new AbortController();
            runtime.cityAbort = controller;

            try {
                const data = await fetchJson(
                    `${apiBase}/np/cities?q=${encodeURIComponent(currentQuery)}`,
                    controller.signal,
                );

                renderDropdown(
                    liveDom.cityDropdown,
                    data.items || [],
                    (ref, label) => {
                        state.city = label;
                        state.cityRef = ref;
                        state.warehouse = "";
                        state.warehouseRef = "";

                        const currentDom = getDom();
                        if (!currentDom) return;

                        setFieldValue(currentDom.fields.city, label);
                        setFieldValue(currentDom.fields.cityRef, ref);
                        currentDom.fields.city.dataset.cityRef = ref;

                        setFieldValue(currentDom.fields.warehouse, "");
                        setFieldValue(currentDom.fields.warehouseRef, "");
                        currentDom.fields.warehouse.dataset.warehouseRef = "";
                        currentDom.fields.warehouse.disabled = false;
                        currentDom.fields.warehouse.placeholder =
                            "Почніть вводити відділення";

                        clearError("city", currentDom);
                        saveStateFromDom(currentDom);
                        closeDropdown(currentDom.cityDropdown);
                        queueCartSave(true);
                    },
                );
            } catch (error) {
                if (error.name !== "AbortError") {
                    const currentDom = getDom();
                    if (currentDom) closeDropdown(currentDom.cityDropdown);
                }
            }
        }, 300);
    }

    function handleWarehouseInput(dom = getDom()) {
        if (!dom || !dom.fields.warehouse || !dom.fields.city) return;

        clearError("warehouse", dom);

        state.warehouse = dom.fields.warehouse.value.trim();
        state.warehouseRef = "";
        setFieldValue(dom.fields.warehouseRef, "");
        dom.fields.warehouse.dataset.warehouseRef = "";
        saveStateFromDom(dom);
        queueCartSave();

        const cityRef = dom.fields.cityRef?.value || state.cityRef || "";
        const query = dom.fields.warehouse.value.trim();

        if (!cityRef || query.length < 1) {
            closeDropdown(dom.warehouseDropdown);
            return;
        }

        clearTimeout(runtime.warehouseTimer);

        if (runtime.warehouseAbort) {
            runtime.warehouseAbort.abort();
        }

        runtime.warehouseTimer = setTimeout(async () => {
            const liveDom = getDom();
            if (!liveDom || !liveDom.fields.warehouse || !liveDom.fields.city) {
                return;
            }

            const currentCityRef =
                liveDom.fields.cityRef?.value || state.cityRef || "";
            const currentQuery = liveDom.fields.warehouse.value.trim();

            if (!currentCityRef || currentQuery.length < 1) {
                closeDropdown(liveDom.warehouseDropdown);
                return;
            }

            const controller = new AbortController();
            runtime.warehouseAbort = controller;

            try {
                const data = await fetchJson(
                    `${apiBase}/np/warehouses?cityRef=${encodeURIComponent(
                        currentCityRef,
                    )}&q=${encodeURIComponent(currentQuery)}`,
                    controller.signal,
                );

                renderDropdown(
                    liveDom.warehouseDropdown,
                    data.items || [],
                    (ref, label) => {
                        state.warehouse = label;
                        state.warehouseRef = ref;

                        const currentDom = getDom();
                        if (!currentDom) return;

                        setFieldValue(currentDom.fields.warehouse, label);
                        setFieldValue(currentDom.fields.warehouseRef, ref);
                        currentDom.fields.warehouse.dataset.warehouseRef = ref;

                        clearError("warehouse", currentDom);
                        saveStateFromDom(currentDom);
                        closeDropdown(currentDom.warehouseDropdown);
                        queueCartSave(true);
                    },
                );
            } catch (error) {
                if (error.name !== "AbortError") {
                    const currentDom = getDom();
                    if (currentDom) closeDropdown(currentDom.warehouseDropdown);
                }
            }
        }, 300);
    }

    function validate(dom = getDom()) {
        if (!dom) return false;

        saveStateFromDom(dom);
        clearAllErrors(dom);

        // canonicalise the phone (handles autofill/paste) before validating
        state.phone = formatPhone(state.phone);
        setFieldValue(dom.fields.phone, state.phone);

        let valid = true;

        if (!state.name.trim()) {
            setError("name", "Введіть ім'я", dom);
            valid = false;
        }

        if (!state.lastname.trim()) {
            setError("lastname", "Введіть прізвище", dom);
            valid = false;
        }

        if (!isValidPhone(state.phone)) {
            setError(
                "phone",
                "Введіть коректний номер: +380 XX XXX XX XX",
                dom,
            );
            valid = false;
        }

        if (!isValidEmail(state.email)) {
            setError("email", "Введіть коректний email", dom);
            valid = false;
        }

        if (!state.city.trim() || !state.cityRef) {
            setError("city", "Оберіть місто зі списку", dom);
            valid = false;
        }

        if (!state.warehouse.trim() || !state.warehouseRef) {
            setError("warehouse", "Оберіть відділення зі списку", dom);
            valid = false;
        }

        return valid;
    }

    async function collectPayload(dom = getDom()) {
        if (!dom) throw new Error("Форма не знайдена");

        saveStateFromDom(dom);

        const cartRes = await fetch("/cart.js", {
            method: "GET",
            headers: { Accept: "application/json" },
        });

        if (!cartRes.ok) {
            throw new Error("Cart fetch failed");
        }

        const cart = await cartRes.json();
        const paymentType = state.payment || "prepay";
        const subtotal = Number(
            cart?.total_price ?? getSubtotalCents(dom) ?? 0,
        );
        const payableNow = paymentType === "prepay" ? prepayAmount : subtotal;

        return {
            customer: {
                name: state.name.trim(),
                lastname: state.lastname.trim(),
                phone: phoneToE164(state.phone) || state.phone.trim(),
                email: state.email.trim(),
            },
            shipping: {
                provider: "nova_poshta",
                city: state.city.trim(),
                cityRef: state.cityRef || null,
                warehouse: state.warehouse.trim(),
                warehouseRef: state.warehouseRef || null,
            },
            payment: {
                type: paymentType,
                prepayAmount: paymentType === "prepay" ? prepayAmount : 0,
                payableNow,
            },
            comment: state.comment.trim(),
            attributes: buildAttributesPayload(),
            cart: {
                token: cart.token,
                note: cart.note,
                currency: cart.currency,
                item_count: cart.item_count,
                total_price: cart.total_price,
                original_total_price: cart.original_total_price,
                items_subtotal_price: cart.items_subtotal_price,
                attributes: cart.attributes || {},
                items: cart.items.map((item) => ({
                    key: item.key,
                    id: item.id,
                    variantid: item.variant_id || item.variantid || item.id,
                    productid: item.product_id || item.productid,
                    sku: item.sku,
                    title: item.title,
                    varianttitle: item.variant_title || item.varianttitle,
                    vendor: item.vendor,
                    quantity: item.quantity,

                    price: Number(item.price || 0),
                    finalprice: Number(
                        item.final_price ?? item.finalprice ?? item.price ?? 0,
                    ),
                    originalprice: Number(
                        item.original_price ?? item.price ?? 0,
                    ),

                    lineprice: Number(item.line_price ?? item.lineprice ?? 0),
                    finallineprice: Number(
                        item.final_line_price ??
                            item.finallineprice ??
                            item.line_price ??
                            item.lineprice ??
                            0,
                    ),
                    originallineprice: Number(
                        item.original_line_price ??
                            item.line_price ??
                            item.lineprice ??
                            0,
                    ),

                    image: item.image,
                    url: item.url,
                    properties: item.properties || {},
                })),
            },
        };
    }

    async function submitOrder() {
        const dom = getDom();
        if (!dom || runtime.isSubmitting) return;

        saveStateFromDom(dom);

        if (!validate(dom)) return;

        runtime.isSubmitting = true;

        if (dom.submitBtn) {
            dom.submitBtn.disabled = true;
            dom.submitBtn.classList.add("cart-submit-loading");
        }

        try {
            await persistCartAttributes({ force: true });

            const payload = await collectPayload(dom);

            const res = await fetch(`${apiBase}/order/create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            let data = {};
            try {
                data = await res.json();
            } catch (e) {
                data = {};
            }

            if (!res.ok || !data.success) {
                throw new Error(
                    data.message || "Не вдалося оформити замовлення",
                );
            }

            if (data.redirect_url) {
                window.location.href = data.redirect_url;
                return;
            }

            if (data.paymentUrl) {
                window.location.href = data.paymentUrl;
                return;
            }

            throw new Error("Сервер не повернув redirect_url або payment_url");
        } catch (error) {
            alert(error.message || "Сталася помилка при оформленні замовлення");

            const liveDom = getDom();
            if (liveDom?.submitBtn) {
                liveDom.submitBtn.disabled = false;
                liveDom.submitBtn.classList.remove("cart-submit-loading");
            }

            runtime.isSubmitting = false;
        }
    }

    function bindGlobals() {
        if (runtime.globalsBound) return;
        runtime.globalsBound = true;

        document.addEventListener("click", (e) => {
            const dom = getDom();
            if (!dom) return;

            if (!e.target.closest(".ci-autocomplete")) {
                closeAllDropdowns(dom);
            }

            const checkoutBtn = e.target.closest("#custom-checkout-btn");
            if (checkoutBtn) {
                e.preventDefault();
                submitOrder();
            }
        });

        document.addEventListener("keydown", (e) => {
            const dom = getDom();
            if (!dom) return;

            if (e.key === "Escape") {
                closeAllDropdowns(dom);
            }
        });

        document.addEventListener("input", (e) => {
            const dom = getDom();
            if (!dom) return;

            const target = e.target;
            if (!target.closest("#custom-order-form")) return;

            if (target.matches('input[name="attributes[FirstName]"]')) {
                clearError("name", dom);
                saveStateFromDom(dom);
                queueCartSave();
                return;
            }

            if (target.matches('input[name="attributes[LastName]"]')) {
                clearError("lastname", dom);
                saveStateFromDom(dom);
                queueCartSave();
                return;
            }

            if (target.matches('input[name="attributes[Email]"]')) {
                clearError("email", dom);
                saveStateFromDom(dom);
                queueCartSave();
                return;
            }

            if (target.matches('textarea[name="attributes[Comment]"]')) {
                saveStateFromDom(dom);
                queueCartSave();
                return;
            }

            if (target.matches('input[name="attributes[Phone]"]')) {
                const formatted = formatPhone(target.value);
                target.value = formatted;
                clearError("phone", dom);
                saveStateFromDom(dom);
                queueCartSave();
                return;
            }

            if (target.matches('input[name="attributes[City]"]')) {
                handleCityInput(dom);
                return;
            }

            if (target.matches('input[name="attributes[Warehouse]"]')) {
                handleWarehouseInput(dom);
            }
        });

        document.addEventListener("focusin", (e) => {
            const dom = getDom();
            if (!dom) return;

            const target = e.target;
            if (!target.closest("#custom-order-form")) return;

            if (
                target.matches('input[name="attributes[Phone]"]') &&
                !target.value.trim()
            ) {
                target.value = "+380";
                saveStateFromDom(dom);
                queueCartSave();
            }
        });

        document.addEventListener("change", (e) => {
            const dom = getDom();
            if (!dom) return;

            const target = e.target;
            if (!target.closest("#custom-order-form")) return;

            if (target.matches('input[name="payment"]')) {
                state.payment = target.value || "prepay";
                setFieldValue(dom.fields.paymentHidden, state.payment);
                saveStateFromDom(dom);
                updatePaymentSummary(dom);
                queueCartSave(true);
                return;
            }

            // Safari commits autofill via `change`, not `input` — normalise and
            // validate the phone here too so a bad autofilled value is caught.
            if (target.matches('input[name="attributes[Phone]"]')) {
                target.value = formatPhone(target.value);
                saveStateFromDom(dom);
                validatePhoneField(dom);
                queueCartSave(true);
                return;
            }

            if (
                target.matches('input[name="attributes[FirstName]"]') ||
                target.matches('input[name="attributes[LastName]"]') ||
                target.matches('input[name="attributes[Email]"]') ||
                target.matches('textarea[name="attributes[Comment]"]')
            ) {
                saveStateFromDom(dom);
                queueCartSave(true);
            }
        });

        document.addEventListener("focusout", (e) => {
            const dom = getDom();
            if (!dom) return;

            const target = e.target;
            if (!target.closest("#custom-order-form")) return;

            if (target.matches('input[name="attributes[Phone]"]')) {
                target.value = formatPhone(target.value);
                saveStateFromDom(dom);
                validatePhoneField(dom);
                queueCartSave();
            }
        });
    }

    function hydrateFromDom() {
        const dom = getDom();
        if (!dom) return;

        initStateFromDom(dom);
        applyStateToDom(dom);
    }

    function watchReRender() {
        if (runtime.syncTimer) return;

        runtime.syncTimer = setInterval(() => {
            const dom = getDom();
            if (!dom) return;

            applyStateToDom(dom);
        }, 250);
    }

    function boot() {
        const dom = getDom();
        if (!dom) return;

        initStateFromDom(dom);
        bindGlobals();
        applyStateToDom(dom);
        watchReRender();
    }

    if (document.readyState === "loading") {
        document.addEventListener(
            "DOMContentLoaded",
            () => {
                boot();
                hydrateFromDom();
            },
            { once: true },
        );
    } else {
        boot();
        hydrateFromDom();
    }
})();
